// src/lib/aiConfig.js
// Configuración central de modelos IA usados en MiPAE.
//
// Ruteo por tarea: el costo del modelo más capaz se paga solo donde la calidad
// del razonamiento compone. Las capas livianas (detectores, resúmenes,
// validaciones, Revisa simple, NOC) van en Haiku; las de razonamiento clínico
// (NANDA, NIC, Revisa+, diálogo socrático) van en Sonnet.

// IDs de modelo — alias sin sufijo de fecha (el alias resuelve al snapshot
// vigente).
export const AI_MODEL_HAIKU = "claude-haiku-4-5";
export const AI_MODEL_SONNET = "claude-sonnet-5";
// Exclusivo de la prueba explícita de redacción por sistemas. No modifica
// el ruteo habitual ni los modelos del PAE.
export const AI_MODEL_PULIDO_SISTEMAS = "claude-haiku-5-5";

// El PAE queda CLAVADO en Sonnet 4.6 a propósito (decisión del fundador).
// El pipeline de diagnóstico es la superficie clínica validada del producto:
// tiene baseline semanal, suite R1→R6 y re-llamada clínica calibrados CONTRA
// ESTE modelo. Cambiarle el modelo por debajo invalida esa validación, y el
// PAE no necesita la mejora: ya está bien cubierto. El resto de la app sí sube
// a Sonnet 5, donde la ganancia de calidad no arrastra riesgo clínico.
//
// Corolario práctico: como 4.6 acepta sampling y no piensa por defecto, el
// request del PAE queda IDÉNTICO al de hoy — `temperature: 0.2` incluido, vía
// las guardas de más abajo. La migración no toca el PAE ni un byte.
export const AI_MODEL_SONNET_PAE = "claude-sonnet-4-6";

// Modelo por defecto: capas livianas y callers que aún no pasan capa.
export const AI_MODEL_ID = AI_MODEL_HAIKU;

// Capas que justifican el modelo de razonamiento (Sonnet). El resto cae a Haiku.
const CAPAS_RAZONAMIENTO = new Set([
  "diagnosticos",
  "actividades_nic",
  "revisa_plus_query",
  "revisa_plus_followup",
  "bitacora_socratico",
  "evolucion_estructurar",
  "evolucion_pulir",
  "entrega_pulir",
  // La revisión de coherencia antes de entregar (pieza 3 de "más toque de
  // IA", 2026-08-01): lectura clínica del documento completo, pide criterio.
  "entrega_revision",
  "examen_pulir",
  "ficha_tutor",
  "quiz_generar",
  "turno_dictado",
  "companero_global",
  // POC MiPAE Voice (/labs/voz): ordenar una narración libre por sistemas sin
  // inventar hallazgos pide criterio extractivo, no plantilla — Sonnet.
  "labs_voz_estructura",
  // PAE con tu pauta (2026-09-16): re-expresa el PAE YA validado en la pauta
  // de la universidad. Pide criterio de redacción, no de diagnóstico: Sonnet
  // 5, y a propósito FUERA de CAPAS_PAE (no re-diagnostica, no exige
  // re-baseline del motor clínico). Fijado por test.
  "pae_pauta",
  // Panel admin (2026-09-27): agrupar las preguntas a Benito por NECESIDAD
  // pide criterio de lectura. Solo la llama un botón del panel de métricas;
  // no cuenta cuota de nadie.
  "admin_necesidades",
]);

// Capas del pipeline del PAE que corren en Sonnet. Se quedan en 4.6 (ver
// AI_MODEL_SONNET_PAE). Las otras capas del PAE (`temporalidad_noc`,
// `capa_4_validation`, `resumen_clinico`) ya viven en Haiku y no se tocan.
const CAPAS_PAE = new Set([
  "diagnosticos",
  "actividades_nic",
]);

// Devuelve el modelo a usar según la capa de la llamada. Sin capa → Haiku.
// Tres destinos: el PAE se queda en Sonnet 4.6 (validado), el resto del
// razonamiento sube a Sonnet 5, y lo liviano sigue en Haiku.
export function modelForCapa(capa) {
  if (!capa || !CAPAS_RAZONAMIENTO.has(capa)) return AI_MODEL_HAIKU;
  return CAPAS_PAE.has(capa) ? AI_MODEL_SONNET_PAE : AI_MODEL_SONNET;
}

// ¿El modelo acepta output_config.effort? Sonnet 4.6+ sí; Haiku 4.5 lo rechaza
// con error de API ("This model does not support the effort parameter"). Ese
// rechazo mató en producción el routing por consulta de Benito (#524): cada
// vez que la consulta bajaba a Haiku, la llamada moría — por eso el effort
// debe condicionarse al modelo resuelto, no mandarse siempre.
export function modeloSoportaEffort(modelo) {
  return typeof modelo === 'string' && modelo.startsWith('claude-sonnet');
}

// La familia 5 (Sonnet 5, Opus 5, Fable 5) cambia DOS contratos respecto de 4.x.
// Ambos se normalizan en `lib/ai.js` sobre el modelo ya resuelto, para que
// ningún call-site tenga que acordarse — misma lección de #524/#571: un
// parámetro que el modelo no entiende no se manda "por si acaso", se guarda.
// `haiku` va incluido aunque hoy no exista un Haiku 5: si algún día se sube
// AI_MODEL_HAIKU y la familia no estuviera contemplada acá, volverían los dos
// bugs silenciosos de esta migración (temperature rechazada, thinking comiéndose
// el max_tokens) justo en las capas de presupuesto más corto. `claude-haiku-4-5`
// no matchea, así que incluirlo hoy no cambia nada — solo cierra la puerta.
function esFamilia5(modelo) {
  return /^claude-(sonnet|opus|haiku|fable)-5\b/.test(String(modelo || ''));
}

// Contrato 1 — sampling. Sonnet 4.6 y Haiku 4.5 aceptan temperature/top_p/top_k;
// la familia 5 los RECHAZA con error de API. Cuatro call-sites del PAE los
// mandan desde antes de que eso cambiara (PAERapido, StepDiagnosis,
// actividadesNIC y el runner clínico): sin esta guarda, migrar a Sonnet 5 caía
// el PAE Rápido, el Detallado y el baseline semanal de una.
// Ante un modelo desconocido devuelve true: preserva el comportamiento de los
// call-sites Haiku, que siguen siendo válidos.
export function modeloAceptaSampling(modelo) {
  return !esFamilia5(modelo);
}

// Contrato 2 — thinking. En Sonnet 4.6 omitir `thinking` significaba NO pensar;
// en la familia 5 omitirlo equivale a thinking adaptativo. El pensamiento se
// descuenta del MISMO max_tokens que la respuesta, así que en las capas con
// presupuesto ajustado (ficha_tutor 700, quiz_generar 900, diagnosticos 1100,
// turno_dictado 1200) el razonamiento se come el texto y trunca a la mitad.
//
// Decisión: lo apagamos EXPLÍCITAMENTE en toda la app. El candidato natural a
// dejarlo prendido era `diagnosticos` (un dx enfermero mejora si el modelo
// razona), pero PAE Rápido llama SIN streaming contra un abort de 25 s y el
// Edge de api/claude.js corta a 25 s: sumar latencia de razonamiento ahí
// cambia una mejora de calidad por timeouts en producción. Prenderlo en
// diagnósticos es una ola aparte, que primero necesita streaming en PAE Rápido
// y presupuestos revisados. No prenderlo suelto acá.
export function thinkingParaModelo(modelo) {
  return esFamilia5(modelo) ? { type: 'disabled' } : null;
}
